import { useState } from "react";
import axios from "axios";
import { FileText, Loader2, Send, Eye, Download } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ["MXN", "USD", "EUR", "GBP", "ARS", "COP", "PEN", "CLP"];
const METHODS = [
  "Transferencia bancaria",
  "Depósito bancario",
  "Efectivo",
  "PayPal",
  "Western Union",
  "MoneyGram",
  "Otro",
];

/**
 * SendReceiptModal — admin captures receipt details, previews the rendered
 * image (no seer name appears in it), and sends it as a chat image to the
 * client. Open by passing a non-null `target` user; close with setTarget(null).
 */
export const SendReceiptModal = ({ target, setTarget }) => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState(() => ({
    client_name: target?.name || target?.username || "",
    service: "",
    amount: "",
    currency: "MXN",
    payment_method: "Transferencia bancaria",
    issue_date: "",
    notes: "",
    extra_message: "",
    receipt_number: "",
  }));
  const [preview, setPreview] = useState(null); // { media_b64, receipt_number, issue_date }
  const [busy, setBusy] = useState(false);

  // Re-seed client name when target changes
  if (target && form.client_name === "" && (target.name || target.username)) {
    setForm((f) => ({ ...f, client_name: target.name || target.username }));
  }

  const close = () => {
    setTarget(null);
    setPreview(null);
    setForm({
      client_name: "",
      service: "",
      amount: "",
      currency: "MXN",
      payment_method: "Transferencia bancaria",
      issue_date: "",
      notes: "",
      extra_message: "",
      receipt_number: "",
    });
  };

  const buildPayload = () => ({
    client_name: form.client_name.trim(),
    service: form.service.trim(),
    amount: Number(form.amount),
    currency: form.currency,
    payment_method: form.payment_method.trim(),
    issue_date: form.issue_date.trim() || undefined,
    notes: form.notes.trim() || undefined,
    extra_message: form.extra_message.trim() || undefined,
    receipt_number: form.receipt_number.trim() || undefined,
  });

  const validate = () => {
    if (!form.client_name.trim()) return "Falta el nombre del cliente";
    if (!form.service.trim()) return "Falta el servicio o concepto";
    if (!form.amount || Number(form.amount) <= 0) return "Monto inválido";
    return null;
  };

  const generatePreview = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${target.id}/receipt/preview`,
        buildPayload(),
        { headers: authHeader() },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la vista previa");
    } finally {
      setBusy(false);
    }
  };

  const sendReceipt = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${target.id}/receipt`,
        buildPayload(),
        { headers: authHeader() },
      );
      toast.success(`🧾 Recibo ${data.receipt_number} enviado a ${form.client_name}`);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el recibo");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!preview?.media_b64) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${preview.media_b64}`;
    a.download = `recibo-${preview.receipt_number || "crisangel"}.png`;
    a.click();
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        data-testid="send-receipt-modal"
        className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#d4af37]" />
            Enviar recibo de pago {target ? `· ${target.name || target.username}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-300">Cliente *</Label>
            <Input
              data-testid="receipt-client-name"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="Nombre completo del cliente"
              className="bg-black/50 border-white/10"
            />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-300">Concepto / Servicio *</Label>
            <Input
              data-testid="receipt-service"
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
              placeholder="Ej: Trabajo de Magia Blanca, Lectura Completa con Runas..."
              className="bg-black/50 border-white/10"
            />
          </div>

          <div>
            <Label className="text-xs text-neutral-300">Monto *</Label>
            <Input
              data-testid="receipt-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="2600.00"
              className="bg-black/50 border-white/10"
            />
          </div>

          <div>
            <Label className="text-xs text-neutral-300">Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="receipt-currency" className="bg-black/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-neutral-300">Método de pago</Label>
            <Select
              value={form.payment_method}
              onValueChange={(v) => setForm({ ...form, payment_method: v })}
            >
              <SelectTrigger data-testid="receipt-method" className="bg-black/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-neutral-300">Fecha (opcional)</Label>
            <Input
              data-testid="receipt-date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              placeholder={`Ej: ${new Date().getDate()} ${["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"][new Date().getMonth()]} ${new Date().getFullYear()}`}
              className="bg-black/50 border-white/10"
            />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-300">N° de recibo (opcional · se genera automático si lo dejas vacío)</Label>
            <Input
              data-testid="receipt-number"
              value={form.receipt_number}
              onChange={(e) => setForm({ ...form, receipt_number: e.target.value })}
              placeholder="CA-2026-000123"
              className="bg-black/50 border-white/10"
            />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-300">Notas (opcional · aparecen en el recibo)</Label>
            <Textarea
              data-testid="receipt-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Ej: Pago en una sola exhibición. Garantía 30 días."
              className="bg-black/50 border-white/10"
            />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-300">Mensaje adicional (opcional · va arriba de la imagen en el chat)</Label>
            <Textarea
              data-testid="receipt-extra-message"
              value={form.extra_message}
              onChange={(e) => setForm({ ...form, extra_message: e.target.value })}
              rows={2}
              placeholder="Ej: Adjunto tu recibo. Cualquier duda, aquí estoy."
              className="bg-black/50 border-white/10"
            />
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <div className="mt-4 rounded-xl border border-[#d4af37]/30 bg-black/40 p-3" data-testid="receipt-preview">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-neutral-400">
                Vista previa · N° <span className="text-[#d4af37] font-mono">{preview.receipt_number}</span>
              </div>
              <button
                onClick={download}
                data-testid="receipt-download"
                className="text-[11px] px-2 py-1 rounded border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> Descargar
              </button>
            </div>
            <img
              alt="vista previa del recibo"
              src={`data:image/png;base64,${preview.media_b64}`}
              className="w-full rounded-lg border border-white/5"
            />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-4 mt-2 border-t border-white/10">
          <button
            onClick={close}
            className="text-xs px-3 py-2 rounded border border-white/10 text-neutral-300 hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            data-testid="receipt-preview-btn"
            onClick={generatePreview}
            disabled={busy}
            className="text-xs px-3 py-2 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            {preview ? "Regenerar vista previa" : "Ver vista previa"}
          </button>
          <button
            data-testid="receipt-send-btn"
            onClick={sendReceipt}
            disabled={busy}
            className="text-xs px-4 py-2 rounded-full bg-[#d4af37] text-black hover:bg-[#e6c350] font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Enviar recibo al chat
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
